// How many addresses in an invite batch actually consume conversation
// allowance. Mirrors partitionInvites in lambda/shared/dialog-invites.ts:
// identity is the NORMALISED email on both sides, a repeat inside one batch is
// charged once, and any address that already has a respondent row costs
// nothing — including a revoked one, which reactivates free.
//
// Counting raw entries instead blocks what the server would happily accept:
// refreshing an existing respondent's context, or reactivating someone who was
// revoked, both of which reserve nothing and stay legal at 0 remaining.
//
// The rule is duplicated rather than imported because src/ and lambda/ are
// separate TypeScript projects. The server's transactional gate is
// authoritative; this only decides what the form lets you attempt.

export function normalizeInviteEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Splits the free-text field the same way the submit handler does.
export function parseInviteEmails(input: string): string[] {
  return input
    .split(/[,\n]/)
    .map((e) => e.trim())
    .filter(Boolean);
}

export function countFreshInvites(args: {
  emails: string[];
  respondents: { email?: string | null }[];
}): number {
  const held = new Set<string>();
  for (const r of args.respondents) {
    if (typeof r.email !== 'string') continue;
    const identity = normalizeInviteEmail(r.email);
    if (identity) held.add(identity);
  }

  const seen = new Set<string>();
  let fresh = 0;
  for (const raw of args.emails) {
    const identity = normalizeInviteEmail(raw);
    if (!identity || seen.has(identity)) continue;
    seen.add(identity);
    if (!held.has(identity)) fresh += 1;
  }
  return fresh;
}
